import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wordmark } from "./Brand";
import { useLang } from "../../i18n";
import { EASE } from "./Reveal";

const links = [
  { id: "story", href: "#story" },
  { id: "founder", href: "#founder" },
  { id: "lookbook", href: "#lookbook" },
  { id: "custom", href: "#custom" },
  { id: "contact", href: "#contact" },
];

export function Header() {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setMenuOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
        className={`fixed top-0 left-0 right-0 z-[9000] transition-colors duration-500 ${
          scrolled ? "bg-[#faf9f6]/85 backdrop-blur-md border-b border-black/5" : "bg-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-[72px] flex items-center justify-between">
          <Wordmark onClick={() => go("#top")} />

          <nav className="hidden md:flex items-center gap-9">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-${l.id}`}
                onClick={() => go(l.href)}
                className="eyebrow link-underline text-[var(--ink-2)] hover:text-[var(--ink)] transition-colors"
              >
                {t.nav[l.id]}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <button
              data-testid="lang-toggle"
              onClick={toggle}
              className="flex items-center gap-1 font-body text-xs tracking-widest"
            >
              <span className={lang === "ro" ? "text-[var(--gold-dark)]" : "text-[var(--ink-3)]"}>RO</span>
              <span className="text-[var(--ink-3)]">/</span>
              <span className={lang === "en" ? "text-[var(--gold-dark)]" : "text-[var(--ink-3)]"}>EN</span>
            </button>
            <button
              data-testid="menu-toggle"
              onClick={() => setMenuOpen((o) => !o)}
              className="md:hidden flex flex-col gap-[5px] w-6"
              aria-label="Menu"
            >
              <span className={`h-px bg-[var(--ink)] transition-all ${menuOpen ? "rotate-45 translate-y-[6px]" : ""}`} />
              <span className={`h-px bg-[var(--ink)] transition-all ${menuOpen ? "opacity-0" : ""}`} />
              <span className={`h-px bg-[var(--ink)] transition-all ${menuOpen ? "-rotate-45 -translate-y-[6px]" : ""}`} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[8999] bg-[#faf9f6] md:hidden flex flex-col items-center justify-center gap-8"
            data-testid="mobile-menu"
          >
            {links.map((l, i) => (
              <motion.button
                key={l.id}
                data-testid={`mobile-nav-${l.id}`}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 + i * 0.06, ease: EASE }}
                onClick={() => go(l.href)}
                className="font-display text-4xl tracking-wide"
              >
                {t.nav[l.id]}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
